import React, { useState } from 'react';
import { ToolUseBlock } from '../../store/chatStore';
import { ToolUseContent } from '../MessageContent/ToolUseContent';
import { getSourceIcon } from '../../utils/sourceIcons';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

import { useTranslation } from 'react-i18next';

interface SourcesPopupProps {
  toolUses: ToolUseBlock[];
}

export const SourcesPopup: React.FC<SourcesPopupProps> = ({ toolUses }) => {
  const { t } = useTranslation('pleri/chat');
  const [showPopup, setShowPopup] = useState(false);

  // Get unique sources from tool uses, with fallback logic
  const sources = Array.from(
    new Set(
      toolUses
        .map((tool) => {
          const metaSource = (tool.meta as { source?: string })?.source;
          if (metaSource) {
            return metaSource;
          }
        })
        .filter(Boolean),
    ),
  );

  if (sources.length === 0) {
    return null;
  }

  return (
    <div
      className="relative"
      onMouseEnter={() => setShowPopup(true)}
      onMouseLeave={() => setShowPopup(false)}
    >
      <div className="flex cursor-pointer items-center gap-2">
        <span className="flex gap-1 text-xs font-medium text-gray-500 dark:text-gray-400">
          <MagnifyingGlassIcon className="h-4 w-4" />
          <span>{t('sourcesLabel')}</span>
        </span>
        <div className="relative flex items-center">
          {sources.slice(0, 4).map((source, index) => {
            const icon = getSourceIcon(source!);
            return (
              <div
                key={source}
                className={`relative flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-gray-100 dark:bg-gray-800 ${
                  index > 0 ? '-ml-1' : ''
                }`}
                style={{ zIndex: index + 1 }}
              >
                {icon || (
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                    {source!.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
            );
          })}
          {sources.length > 3 && (
            <div
              className="relative -ml-2 flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-gray-50 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-400"
              style={{ zIndex: 5 }}
            >
              +{sources.length - 3}
            </div>
          )}
        </div>
      </div>
      {/* Hover Popup */}
      {showPopup && (
        <div className="absolute bottom-full left-[-50px] z-99999999 w-[500px] rounded-lg border bg-white p-4 pr-0 dark:bg-gray-900">
          {/* Bridge to prevent gap */}
          <div className="absolute top-full h-2 w-full"></div>
          <div className="space-y-3">
            <div className="border-b border-gray-100 pb-2 dark:border-gray-800">
              <h3 className="flex items-center gap-2 font-medium text-gray-900 dark:text-gray-100">
                {t('sources')}
              </h3>
            </div>

            <div className="max-h-64 space-y-2 overflow-y-auto pr-4">
              {toolUses
                .filter((tool) => {
                  const metaSource = (tool.meta as { source?: string })?.source;
                  return metaSource;
                })
                .map((tool) => (
                  <div key={tool.id}>
                    <ToolUseContent
                      content={tool}
                      preventToolUseBlockToCollapse={true}
                    />
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
